import * as THREE from 'three'
import Experience from '../Experience.js'
import Lenis from 'lenis'
import { gsap } from 'gsap'
import IntroSequence from './IntroSequence.js'

// Visite guidée au scroll : un plan-séquence par trou (CourseRoute), un
// fondu au noir entre deux plans, et les modes de vue (guidée, libre,
// plan). L'ouverture (descente d'intro) est déléguée à IntroSequence.
export default class Timeline {
    constructor() {
        this.experience = new Experience()
        this.camera = this.experience.camera
        this.environment = this.experience.world.environment
        this.scene = this.experience.scene
        this.time = this.experience.time
        this.ui = this.experience.ui
        this.labels = this.experience.labels
        this.ballTrajectory = this.experience.ballTrajectory
        this.info = this.experience.info
        this.debug = this.experience.debug

        const cameraConfig = this.experience.config.camera
        const courseConfig = this.experience.config.course
        this.config = cameraConfig.timeline
        this.cinematicConfig = cameraConfig.cinematic?.drift ?? { enabled: false }
        this.cinematicFovConfig = cameraConfig.cinematic?.fovBreathing ?? { enabled: false }
        this.bankingConfig = cameraConfig.cinematic?.banking ?? { enabled: false }
        this.speedFovConfig = cameraConfig.cinematic?.speedFov ?? { enabled: false }
        this.progressDamping = this.config.progressDamping ?? 0.04

        // Mouvements purement décoratifs coupés si l'utilisateur le demande.
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
            this.cinematicConfig = { enabled: false }
            this.cinematicFovConfig = { enabled: false }
            this.bankingConfig = { enabled: false }
            this.speedFovConfig = { enabled: false }
        }

        this.holesConfig = this.experience.config.holes
        this.holeNumbers = Object.keys(this.holesConfig).map(Number).sort((a, b) => a - b)
        this.firstHole = this.holeNumbers[0]
        this.teeSelectionTrigger = this.holesConfig[this.firstHole][0] - 0.01

        this.shotEasing = courseConfig.shotEasing ?? 0.6
        this.fadeWidth = courseConfig.fadeWidth ?? 0.006
        this.holdWidth = courseConfig.holdWidth ?? 0.004
        this.fadeElement = document.querySelector('.hole-fade')
        this.fadeOpacity = -1
        this.currentSegment = null
        this.currentHole = null

        this.debugState = { progress: 0, targetProgress: 0 }
        this.progress = 0
        this.targetProgress = 0
        this.paused = false
        this.baseFov = this.camera.instance.fov

        this.shotPosition = new THREE.Vector3()
        this.shotTarget = new THREE.Vector3()
        this.tempPosition = new THREE.Vector3()
        this.tempTarget = new THREE.Vector3()
        this.tempDirection = new THREE.Vector3()
        this.tempQuaternion = new THREE.Quaternion()

        // État cinématique (roulis dans les virages, focale liée à la vitesse)
        this.lastHeading = null
        this.roll = 0
        this.fovKick = 0
        this.lastCameraProgress = 0

        // Fondu de pose (retour de l'exploration libre, changement de départ)
        this.freeReturn = null
        this.freeReturnDuration = 1.6

        if (this.debug.active) this.syncDebugState()
        this.setCurves()
        this.setLenis()
        this.setDebug()
        this.intro = new IntroSequence(this)
        this.setHashNavigation()
    }

    get introState() {
        return this.intro?.state ?? 'idle'
    }

    //
    // SCROLL
    //

    setLenis() {
        window.scrollTo(0, 0)
        history.scrollRestoration = 'manual'

        const isMobile = this.experience.isMobile
        const scroll = this.config?.scroll ?? {}

        this.lenis = new Lenis({
            wrapper: window,
            content: document.body,
            lerp: isMobile ? scroll.lerpMobile : scroll.lerp,
            wheelMultiplier: scroll.wheelMultiplier,
            syncTouch: isMobile,
            syncTouchLerp: isMobile ? scroll.syncTouchLerpMobile : scroll.syncTouchLerp,
            touchMultiplier: isMobile ? scroll.touchMultiplierMobile : scroll.touchMultiplier,
        })

        this.lenis.scrollTo(0, { immediate: true })
        this.lenis.stop()

        this.lenis.on('scroll', () => {
            if (this.paused || !this.intro?.isDone) return

            // La visite commence au trou 1 : pas de retour vers l'écran
            // d'accueil en remontant. (scrollTo réémet « scroll » de façon
            // synchrone : garde-fou contre la réentrance, et tolérance pour
            // l'arrondi en pixels.)
            const minProgress = this.getHoleEntryProgress(this.firstHole)
            let progress = this.lenis.scroll / this.lenis.limit
            if (progress < minProgress) {
                progress = minProgress
                if (!this.clampingScroll && minProgress - this.lenis.scroll / this.lenis.limit > 1 / this.lenis.limit) {
                    this.clampingScroll = true
                    this.lenis.scrollTo(Math.ceil(minProgress * this.lenis.limit), { immediate: true })
                    this.clampingScroll = false
                }
            }

            this.targetProgress = progress
            this.ui?.onScrollActivity?.()
        })
    }

    // Début de la visite guidée (fin de l'intro ou lien direct vers un trou)
    beginTour(progress, { blendFromCurrent = false } = {}) {
        const pose = blendFromCurrent ? this.capturePose() : null

        this.paused = false
        this.lenis?.start()
        this.jumpTo(progress)
        this.currentSegment = null
        this.fadeOpacity = -1
        this.freeReturn = pose
    }

    // « Revoir le parcours » : fondu au noir, retour au départ du trou 1
    restartTour() {
        if (this.viewMode !== 'guided') return

        gsap.killTweensOf(this.fadeElement)
        gsap.to(this.fadeElement, {
            opacity: 1,
            duration: 0.5,
            ease: 'power2.in',
            onComplete: () => {
                this.jumpTo(this.getHoleEntryProgress(this.firstHole))
                gsap.to(this.fadeElement, {
                    opacity: 0,
                    duration: 0.8,
                    ease: 'power2.out',
                    onComplete: () => { this.fadeOpacity = -1 }
                })
            }
        })
    }

    pauseForTeeSelection() {
        if (this.paused) return

        this.paused = true

        // Discard scroll ahead of the smoothed camera position so resume
        // does not jump forward after tee selection.
        this.targetProgress = this.progress
        if (this.lenis?.limit) {
            this.lenis.scrollTo(this.progress * this.lenis.limit, { immediate: true })
        }

        this.lenis?.stop()
        this.experience.teeSelection?.show()

        if (this.debug.active) this.syncDebugState()
    }

    resume() {
        if (this.introState === 'awaitingTee') {
            this.intro.finish()
            return
        }

        if (!this.paused) return

        this.paused = false
        this.lenis?.start()
    }

    checkTeeSelectionGate() {
        if (this.experience.teeSelection?.selectedColor) return
        if (this.paused) return

        if (this.targetProgress >= this.teeSelectionTrigger || this.progress >= this.teeSelectionTrigger) {
            this.pauseForTeeSelection()
        }
    }

    // Saut direct à une progression (plan, lien direct), sans traversée.
    jumpTo(progress) {
        const p = THREE.MathUtils.clamp(progress, 0, 1)
        this.progress = p
        this.targetProgress = p
        this.lastCameraProgress = p
        this.lastHeading = null
        this.freeReturn = null

        if (this.lenis?.limit) {
            this.lenis.scrollTo(p * this.lenis.limit, { immediate: true, force: true })
        }
    }

    //
    // LIEN DIRECT VERS UN TROU (#trou-7)
    //

    parseHoleHash(hash = window.location.hash) {
        const match = /^#trou-?(\d+)$/i.exec(hash)
        const hole = match ? Number(match[1]) : null
        return this.holesConfig[hole] ? hole : null
    }

    getHoleUrl(hole) {
        return `${window.location.origin}${window.location.pathname}#trou-${hole}`
    }

    setHashNavigation() {
        const hole = this.parseHoleHash()
        if (hole != null) this.intro.skipToHole(hole)

        // Adresse modifiée à la main pendant la visite
        this.onHashChange = () => {
            const target = this.parseHoleHash()
            if (target == null || target === this.currentHole) return
            if (!this.intro.isDone) this.intro.skipToHole(target)
            else this.experience.modeToggle?.goToHole(target)
        }
        window.addEventListener('hashchange', this.onHashChange)
    }

    // L'adresse suit le trou affiché, prête à être partagée
    syncHash(hole) {
        const hash = hole != null ? `#trou-${hole}` : ''
        if (window.location.hash === hash) return
        history.replaceState(null, '', `${window.location.pathname}${window.location.search}${hash}`)
    }

    //
    // MODES DE VUE
    //

    // 'guided' (scroll), 'free' (OrbitControls) ou 'map' (plan orthographique)
    get viewMode() {
        const mode = this.camera.controlMode
        if (mode === 'orbit') return 'free'
        if (mode === 'map') return 'map'
        return 'guided'
    }

    setViewMode(mode, { blend = true } = {}) {
        const from = this.viewMode
        if (mode === from || !this.hasValidCurves) return

        if (from === 'guided') this.suspendGuided()
        if (from === 'map') this.experience.mapView?.hide()

        if (mode === 'free') {
            this.camera.setControlMode('orbit')
        } else if (mode === 'map') {
            this.camera.setControlMode('map')
            this.experience.mapView?.show()
        } else {
            this.resumeGuided(blend)
        }

        this.updateCutFade(this.progress)
    }

    // La visite est figée à sa progression actuelle (scroll suspendu) ;
    // l'exploration libre orbitera autour du point que la caméra regardait.
    suspendGuided() {
        this.freeReturn = null
        this.targetProgress = this.progress
        if (this.lenis?.limit) {
            this.lenis.scrollTo(this.progress * this.lenis.limit, { immediate: true })
        }
        this.lenis?.stop()

        this.camera.setOrbitTarget(this.tempTarget)
    }

    // Retour au parcours guidé, là où la visite avait été laissée : la
    // caméra rejoint la trajectoire par un fondu de position/orientation.
    resumeGuided(blend) {
        this.freeReturn = blend ? this.capturePose() : null

        this.camera.setControlMode('scroll')
        this.lastHeading = null
        this.lastCameraProgress = this.progress
        this.targetProgress = this.progress

        if (!this.paused) {
            this.lenis?.start()
            if (this.lenis?.limit) {
                this.lenis.scrollTo(this.progress * this.lenis.limit, { immediate: true })
            }
        }
    }

    capturePose() {
        const cam = this.camera.perspective
        return {
            position: cam.position.clone(),
            quaternion: cam.quaternion.clone(),
            fov: cam.fov,
            t: 0
        }
    }

    //
    // PLANS-SÉQUENCES
    //

    // Plans générés depuis le parcours, pour le départ choisi (départs
    // blancs tant que le joueur n'a pas choisi).
    setCurves(teeColor = 'white') {
        this.segments = this.experience.course.buildSegments(teeColor)
        this.hasValidCurves = this.segments.length > 0

        this.applyInitialCamera()
    }

    // Au choix du départ, les plans sont reconstruits : la caméra rejoint
    // le nouveau plan par le même fondu que l'exploration libre.
    setTeeColor(teeColor) {
        const pose = this.viewMode === 'guided' ? this.capturePose() : null
        this.segments = this.experience.course.buildSegments(teeColor ?? 'white')
        if (pose) this.freeReturn = pose
    }

    getSegment(progress) {
        const segments = this.segments
        for (let i = 0; i < segments.length - 1; i++) {
            if (progress < segments[i].end) return segments[i]
        }
        return segments[segments.length - 1]
    }

    // Lecture d'un plan : départ et arrivée en douceur, vitesse régulière
    // au milieu (mélange linéaire / smootherstep, sans à-coup).
    easeShot(t) {
        const smoother = t * t * t * (t * (t * 6 - 15) + 10)
        return THREE.MathUtils.lerp(t, smoother, this.shotEasing)
    }

    // Avancement du plan dans son segment, avec des temps morts : la
    // caméra reste immobile pendant le fondu d'entrée puis un court
    // instant (holdWidth) avant de partir, et s'arrête avant le fondu de
    // sortie. Le mouvement ne se joue donc jamais sur écran noir.
    getShotLocal(segment, progress) {
        const index = this.segments.indexOf(segment)
        const fadesIn = index > 0 && segment.hole !== this.firstHole
        const fadesOut = index < this.segments.length - 1
        const lead = (fadesIn ? this.fadeWidth : 0) + (segment.hole != null ? this.holdWidth : 0)
        const trail = fadesOut ? this.fadeWidth : 0
        const span = Math.max(1e-6, segment.end - segment.start - lead - trail)

        return THREE.MathUtils.clamp((progress - segment.start - lead) / span, 0, 1)
    }

    // Même avancement, pour un trou donné (tracé de balle)
    getHoleShotProgress(hole, progress) {
        const segment = this.segments?.find(s => s.hole === hole)
        return segment ? this.getShotLocal(segment, progress) : null
    }

    getShotPose(progress, position, target) {
        const segment = this.getSegment(progress)
        const t = this.easeShot(this.getShotLocal(segment, progress))

        segment.positionCurve.getPoint(t, position)
        segment.targetCurve.getPoint(t, target)
        return segment
    }

    // Fondu au noir de part et d'autre de chaque coupe entre deux plans
    updateCutFade(progress) {
        if (!this.fadeElement) return

        let opacity = 0
        if (this.viewMode === 'guided') {
            const segment = this.getSegment(progress)
            // L'intro n'est pas accessible au scroll : pas de fondu à
            // l'entrée du trou 1, où se pose la descente d'ouverture.
            const isFirst = segment === this.segments[0] || segment.hole === this.firstHole
            const isLast = segment === this.segments[this.segments.length - 1]
            const fromStart = isFirst ? Infinity : progress - segment.start
            const toEnd = isLast ? Infinity : segment.end - progress
            const edge = Math.min(fromStart, toEnd)
            opacity = 1 - THREE.MathUtils.smoothstep(edge, 0, this.fadeWidth)
        }

        if (opacity !== this.fadeOpacity) {
            this.fadeOpacity = opacity
            this.fadeElement.style.opacity = opacity.toFixed(3)
        }
    }

    // Progression à laquelle un trou est pleinement visible (après le fondu)
    getHoleEntryProgress(hole) {
        const range = this.holesConfig[hole]
        if (!range) return null
        return hole === this.firstHole ? range[0] : range[0] + this.fadeWidth
    }

    applyInitialCamera() {
        if (!this.hasValidCurves) return

        this.getShotPose(0, this.tempPosition, this.tempTarget)
        this.camera.instance.position.copy(this.tempPosition)
        this.camera.instance.lookAt(this.tempTarget)
    }

    //
    // BOUCLE
    //

    update() {
        this.lenis?.raf(performance.now())

        if (!this.intro.isDone) {
            this.intro.update(this.getDeltaSeconds())
            this.ui.update(this.progress)
            return
        }

        if (this.paused) {
            if (this.camera.controlMode === 'scroll' && this.hasValidCurves) {
                this.updateCamera()
                this.ui.update(this.progress)
            }
            return
        }

        if (this.camera.controlMode !== 'scroll') {
            this.ui?.animateCompass?.()
            return
        }
        if (!this.hasValidCurves) return

        this.checkTeeSelectionGate()
        if (this.paused) return

        // Amortissement exprimé pour 60 fps puis corrigé par le delta réel :
        // même ressenti à 60, 120 ou 144 Hz, et pas d'à-coup sur une frame lente.
        const frames = this.getDeltaSeconds() * 60
        const k = 1 - Math.pow(1 - this.progressDamping, frames)
        this.progress += (this.targetProgress - this.progress) * k

        this.updateCamera()
        this.environment?.applyProgress?.(this.progress)
        this.updateHoleActivation()
        this.ballTrajectory?.update(this.progress)
        this.ui.update(this.progress)

        if (this.debug.active) this.syncDebugState()
    }

    getDeltaSeconds() {
        return THREE.MathUtils.clamp((this.time.delta ?? 16) / 1000, 1 / 240, 0.1)
    }

    // Exponentielle « critique » indépendante du framerate.
    damp(current, target, lambda, dt) {
        return THREE.MathUtils.lerp(current, target, 1 - Math.exp(-lambda * dt))
    }

    // Roulis très léger dans le sens du virage, comme une caméra embarquée
    // sur un drone : donne la sensation de trajectoire sans jamais se voir.
    updateBanking(position, target, dt) {
        if (!this.bankingConfig.enabled) return

        this.tempDirection.subVectors(target, position)
        const heading = Math.atan2(this.tempDirection.x, this.tempDirection.z)

        let yawRate = 0
        if (this.lastHeading !== null) {
            let delta = heading - this.lastHeading
            delta = Math.atan2(Math.sin(delta), Math.cos(delta))
            // Saut (retour au départ, clic sur la timeline) : on ignore.
            if (Math.abs(delta) < 0.35) yawRate = delta / dt
        }
        this.lastHeading = heading

        const max = THREE.MathUtils.degToRad(this.bankingConfig.maxDegrees ?? 1.4)
        const targetRoll = THREE.MathUtils.clamp(yawRate * (this.bankingConfig.gain ?? 0.06), -max, max)
        this.roll = this.damp(this.roll, targetRoll, this.bankingConfig.damping ?? 2.2, dt)

        this.camera.instance.rotateZ(this.roll)
    }

    // Ouverture de focale proportionnelle à la vitesse de défilement.
    updateSpeedFov(dt) {
        if (!this.speedFovConfig.enabled) {
            this.fovKick = 0
            return
        }

        const speed = Math.abs(this.progress - this.lastCameraProgress) / dt
        this.lastCameraProgress = this.progress

        const valid = speed < 0.5
        const max = this.speedFovConfig.maxDegrees ?? 2.5
        const targetKick = valid ? Math.min(max, speed * (this.speedFovConfig.gain ?? 40)) : this.fovKick
        this.fovKick = this.damp(this.fovKick, targetKick, this.speedFovConfig.damping ?? 2.5, dt)
    }

    easeInOutCubic(t) {
        return t < 0.5
            ? 4 * t * t * t
            : 1 - Math.pow(-2 * t + 2, 3) / 2
    }

    updateCamera() {
        const segment = this.getShotPose(this.progress, this.shotPosition, this.shotTarget)
        const position = this.shotPosition
        const target = this.shotTarget

        const isOutro = segment === this.segments[this.segments.length - 1]
        const outroT = isOutro ? (this.progress - segment.start) / (segment.end - segment.start) : 0
        this.environment?.setFogOpening(THREE.MathUtils.smoothstep(outroT, 0, 0.6))

        // Coupe entre deux plans : pas de roulis ni d'élan hérité du précédent
        if (segment !== this.currentSegment) {
            this.currentSegment = segment
            this.lastHeading = null
            this.roll = 0
        }
        this.updateCutFade(this.progress)

        this.tempPosition.copy(position)
        this.tempTarget.copy(target)

        if (this.cinematicConfig.enabled) {
            const elapsed = this.time.elapsed * 0.001
            const f = this.cinematicConfig.frequency ?? 0.2
            const posAmp = this.cinematicConfig.positionAmplitude ?? 0.03
            const lookAmp = this.cinematicConfig.lookAmplitude ?? 0.06

            this.tempPosition.x += Math.sin(elapsed * f * 4.7) * posAmp
            this.tempPosition.y += Math.cos(elapsed * f * 5.3) * (posAmp * 0.55)
            this.tempTarget.x += Math.sin(elapsed * f * 3.1 + 0.7) * lookAmp
            this.tempTarget.y += Math.cos(elapsed * f * 2.6 + 1.1) * (lookAmp * 0.45)
        }

        const dt = this.getDeltaSeconds()

        this.camera.instance.position.copy(this.tempPosition)
        this.camera.instance.lookAt(this.tempTarget)
        this.updateBanking(position, target, dt)
        this.updateSpeedFov(dt)

        if (!this.camera.fovManual) {
            this.updateCinematicFov()
        }

        this.applyFreeReturn(dt)

        this.camera.instance.updateProjectionMatrix()
    }

    applyFreeReturn(dt) {
        if (!this.freeReturn) return

        const ret = this.freeReturn
        ret.t = Math.min(1, ret.t + dt / this.freeReturnDuration)
        const k = this.easeInOutCubic(ret.t)
        const cam = this.camera.instance

        cam.position.lerpVectors(ret.position, cam.position, k)
        this.tempQuaternion.copy(cam.quaternion)
        cam.quaternion.slerpQuaternions(ret.quaternion, this.tempQuaternion, k)
        cam.fov = THREE.MathUtils.lerp(ret.fov, cam.fov, k)

        if (ret.t >= 1) this.freeReturn = null
    }

    updateCinematicFov() {
        let breathing = 0

        if (this.cinematicFovConfig.enabled) {
            const t = this.time.elapsed * 0.001
            const amp = this.cinematicFovConfig.amplitude ?? 1.25
            const freq = this.cinematicFovConfig.frequency ?? 0.16
            breathing = Math.sin(t * freq * Math.PI * 2) * amp
        }

        this.camera.instance.fov = this.baseFov + breathing + this.fovKick
    }

    // Trou affiché : celui du plan en cours (aucun pendant l'intro et
    // l'outro). Met à jour repères, fiche et adresse partageable.
    updateHoleActivation() {
        const hole = this.getSegment(this.progress).hole ?? null

        this.labels.setHoleTransition(hole, null, 0)

        if (hole != null) this.info.setInfo(hole)
        else this.info.clearHighlight()

        if (hole !== this.currentHole) {
            this.currentHole = hole
            if (hole != null) this.syncHash(hole)
        }
    }

    //
    // DEBUG
    //

    setDebug() {
        if (!this.debug.active) return

        this.debugFolder = this.debug.ui.addFolder({ title: 'Timeline', expanded: true })

        this.debugFolder.addBinding(this.debugState, 'progress', {
            label: 'Progress',
            readonly: true,
            format: v => v.toFixed(3)
        })

        this.debugFolder.addBinding(this.debugState, 'targetProgress', {
            label: 'Target',
            readonly: true,
            format: v => v.toFixed(3)
        })
    }

    syncDebugState() {
        this.debugState.progress = this.progress
        this.debugState.targetProgress = this.targetProgress
        this.debug.ui?.refresh?.()
    }

    destroy() {
        this.lenis?.destroy?.()
        this.intro?.destroy()
        window.removeEventListener('hashchange', this.onHashChange)
        gsap.killTweensOf(this.fadeElement)

        this.labels = null
        this.ballTrajectory = null
    }
}
